import DOMPurify from "dompurify"
import LinkifyIt from "linkify-it"
import moment from "moment"
import tlds from "tlds"

// Configure DOMPurify

// 1. Add hook to allow target="_blank" (securely)
// See: https://github.com/cure53/DOMPurify/issues/317
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  // set all elements owning target to target=_blank
  if ("target" in node) {
    node.setAttribute("target", "_blank")
    node.setAttribute("rel", "noopener")
  }

  // Scrub any 'font' or 'font-size' attributes
  if (node?.style?.font) {
    node.style.font = ""
  }
  if (node?.style?.fontSize) {
    node.style.fontSize = ""
  }
})

export const CTDOMPurify = DOMPurify

// Setup LinkifyIt
const linkifier = new LinkifyIt({ fuzzyIP: false, fuzzyEmail: false, fuzzyLink: false })
linkifier.tlds(tlds).add("git:", "https:") // Support git urls as https

export const prettyStringForEnum = (rawString) => {
  let pretty = rawString
  switch (rawString) {
    // Pretty Strings
    default:
      break
  }
  return pretty
}

export const isDatePast = (rawDate) => {
  return moment(rawDate).isBefore() // before now by default
}

export const prettyDate = (rawDate) => {
  return moment(rawDate).format("MMMM Do YYYY")
}

export const prettyDateWithFormat = (rawDate, dateFormat = null) => {
  return moment(rawDate).format(dateFormat ? dateFormat : "MMMM Do YYYY")
}

export const appVersionString = (version) => {
  const APP_BUILD = `${process.env.REACT_APP_BUILD ?? ""}`
  let appVersion = ""
  switch (true) {
    case version?.length > 0 && APP_BUILD?.length > 0:
      appVersion = `${version} (${APP_BUILD})`
      break
    case version?.length > 0:
      appVersion = version
      break
    case APP_BUILD?.length > 0:
      appVersion = APP_BUILD
      break
    default:
      break
  }
  return appVersion
}

// Replaces <token> with 'Value'
export const tokenResolvedString = (sourceString, tokenMap) => {
  // Guard: a missing/optional report field (e.g. an action with no recommendation) can arrive
  // undefined — resolving tokens in "nothing" is a no-op, not a crash (.replaceAll on undefined).
  if (typeof sourceString !== "string") return sourceString
  let sourceWithResolvedTokens = sourceString
  // Tokens looks like
  // {
  //     'some_email_variable': 'enduser@company.com',
  //     'another_email_variable': 'itadmin@company.com'
  // }

  for (const [token, replacementValue] of Object.entries(tokenMap)) {
    // Normalize any whitespace in the token
    // eslint-disable-next-line no-useless-escape
    const normToken = token.replace(/\s/g, "\\s")
    if (replacementValue.value) {
      sourceWithResolvedTokens = sourceWithResolvedTokens.replaceAll(RegExp(`<${normToken}>`, "ig"), `${replacementValue.value}`)
      sourceWithResolvedTokens = sourceWithResolvedTokens.replaceAll(RegExp(`&lt;${normToken}&gt;`, "ig"), `${replacementValue.value}`) // HTML escapes
    } else {
      sourceWithResolvedTokens = sourceWithResolvedTokens.replaceAll(RegExp(`<${normToken}>`, "ig"), `${token}`)
      sourceWithResolvedTokens = sourceWithResolvedTokens.replaceAll(RegExp(`&lt;${normToken}&gt;`, "ig"), `${token}`) // HTML escapes
    }
  }

  return sourceWithResolvedTokens
}

// Replaces <token> with 'Value (token)'
export const userTokenResolvedString = (sourceString, tokenMap) => {
  if (typeof sourceString !== "string") return sourceString
  let sourceWithResolvedTokens = sourceString
  // Tokens looks like
  // {
  //     'End User': 'enduser@company.com',
  //     'I.T. Admin': 'itadmin@company.com'
  // }

  for (const [token, userItem] of Object.entries(tokenMap)) {
    // Normalize any whitespace in the token
    // eslint-disable-next-line no-useless-escape
    const normToken = token.replace(/\s/g, "\\s")
    if (userItem) {
      sourceWithResolvedTokens = sourceWithResolvedTokens.replaceAll(RegExp(`<${normToken}>`, "ig"), `${userItem} (${token})`)
      sourceWithResolvedTokens = sourceWithResolvedTokens.replaceAll(RegExp(`&lt;${normToken}&gt;`, "ig"), `${userItem} (${token})`) // HTML escapes
    } else {
      sourceWithResolvedTokens = sourceWithResolvedTokens.replaceAll(RegExp(`<${normToken}>`, "ig"), `${token}`)
      sourceWithResolvedTokens = sourceWithResolvedTokens.replaceAll(RegExp(`&lt;${normToken}&gt;`, "ig"), `${token}`) // HTML escapes
    }
  }

  return sourceWithResolvedTokens
}

export const resolveUsersAndVariableTokensInStringForDisplay = ({ source, variables = {}, roles = {} }) => {
  return userTokenResolvedString(tokenResolvedString(source, variables), roles)
}

// Modified From: https://stackoverflow.com/a/21350614
export const stringSplice = ({ str, index, count, add }) => {
  // We cannot pass negative indexes directly to the 2nd slicing operation.
  if (index < 0) {
    index = str.length + index
    if (index < 0) {
      index = 0
    }
  }

  return str.slice(0, index) + (add || "") + str.slice(index + count)
}

const safeFromCodePoint = (codePoint) => {
  if (!Number.isFinite(codePoint) || codePoint < 0 || codePoint > 0x10ffff) return ""
  try {
    return String.fromCodePoint(codePoint)
  } catch {
    return ""
  }
}

// Decode numeric HTML character references — decimal (&#60;) and hex (&#x3c;). Named entities are
// handled separately by the callers' entity maps. Decoding numeric refs is required for safe
// stripping: without it an attacker-encoded "&#x3c;script&#x3e;" survives a tag-strip and can
// reappear as live markup once decoded downstream. Decode these BEFORE stripping tags.
export const decodeNumericHtmlEntities = (text) =>
  text
    .replace(/&#x([0-9a-fA-F]+);/g, (_m, hex) => safeFromCodePoint(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_m, dec) => safeFromCodePoint(parseInt(dec, 10)))

// Strip HTML tags repeatedly until the string stops changing. A single global replace is an
// incomplete sanitization: "<scr<b>ipt>" becomes "<script>" after one removal of "<b>", so the
// pattern can be reconstructed. Iterating to a fixed point removes any tags that reform. The
// pattern is anchored to a tag-name start ("<" then a letter, or "</") so decoded prose angle
// brackets like "latency < 5ms > 1ms" are preserved rather than eaten as if they were tags.
export const stripHtmlTagsToFixedPoint = (text, replacement = "") => {
  let out = text
  let prev
  do {
    prev = out
    out = out.replace(/<\/?[a-zA-Z][^>]*>/g, replacement)
  } while (out !== prev)
  return out
}

export const unescapeHTMLEntities = (stringWithHTMLEntities) => {
  const entities = {
    "&amp;": "&",
    "&lt;": "<",
    "&gt;": ">",
    "&apos;": "'",
    "&quot;": '"',
    "&#39;": "'",
  }
  return stringWithHTMLEntities.replace(/&amp;|&lt;|&gt;|&quot;|&#39;|&apos;/g, (m) => entities[m] || m)
}

// AI gameplay replies are produced as a JSON envelope:
//   {"response": "<html…>", "result": "…", "result_rationale": "…"}
// server-node's stream extractor normally hands the client just the decoded
// `response` value, but if that extraction misses an edge case (a ```json
// fence, a reordered field, the non-streamed buffered fallback) the raw
// envelope can reach the UI and render as garbled JSON. This guard pulls the
// decoded `response` value out of such an envelope; anything that isn't a
// recognizable envelope is returned unchanged. It is tolerant of partial
// input (mid-stream): when the closing quote hasn't arrived yet it returns
// whatever has been decoded so far.
const AI_RESPONSE_KEY_PATTERN = /"response"\s*:\s*"/
export const extractAiResponseField = (raw) => {
  if (typeof raw !== "string" || !raw) return raw
  const match = AI_RESPONSE_KEY_PATTERN.exec(raw)
  if (!match) return raw
  let out = ""
  let prevBackslash = false
  for (let i = match.index + match[0].length; i < raw.length; i += 1) {
    const ch = raw[i]
    if (prevBackslash) {
      if (ch === "n") out += "\n"
      else if (ch === "t") out += "\t"
      else if (ch === "r") out += "\r"
      else if (ch === '"') out += '"'
      else if (ch === "\\") out += "\\"
      else if (ch === "/") out += "/"
      else if (ch === "u") {
        const hex = raw.slice(i + 1, i + 5)
        if (hex.length < 4) break // wait for the rest of the \uXXXX escape (streaming)
        const code = parseInt(hex, 16)
        if (!Number.isNaN(code)) out += String.fromCharCode(code)
        i += 4
      } else {
        out += "\\" + ch // unknown escape — keep the literal pair
      }
      prevBackslash = false
    } else if (ch === "\\") {
      prevBackslash = true
    } else if (ch === '"') {
      break // unescaped closing quote — end of the response value
    } else {
      out += ch
    }
  }
  return out
}

export const LINE_BREAK_STYLE = Object.freeze({
  NEWLINE_CHAR: "NEWLINE_CHAR",
  HTML_BREAK: "HTML_BREAK",
})

const LINE_BREAK_PATTERN = Object.freeze({
  NEWLINE_CHAR: "\n",
  HTML_BREAK: /<br\s*\/?>/gi,
})

const LINE_BREAK_VALUE = Object.freeze({
  NEWLINE_CHAR: "\n",
  HTML_BREAK: "<br>",
})

// Normalize all line break expressions in the text
export const normalizeLineBreaks = ({ input, style = LINE_BREAK_STYLE.NEWLINE_CHAR }) => {
  if (typeof input !== "string") return input
  let withNormedNewlines = input
  switch (style) {
    case LINE_BREAK_STYLE.NEWLINE_CHAR: {
      withNormedNewlines = input.replaceAll(LINE_BREAK_PATTERN.HTML_BREAK, LINE_BREAK_VALUE.NEWLINE_CHAR)
      break
    }
    case LINE_BREAK_STYLE.HTML_BREAK: {
      withNormedNewlines = input.replaceAll(LINE_BREAK_PATTERN.NEWLINE_CHAR, LINE_BREAK_VALUE.HTML_BREAK)
      break
    }
    default:
      break
  }
  return withNormedNewlines
}

// TODO: Let's discuss use cases for this function (I don't think we need it)
// Helper to parse plain text to HTML with <p> or <br> for each new line
export const parseTextToHtml = (text) => {
  if (!text) return ""
  // Split by double newlines for paragraphs, single newline for <br>
  return text
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${paragraph.replace(/\n/g, "<br>")}</p>`)
    .join("")
}

// Markdown image -> HTML <img>.
//
// The GAME renders scenario text as: linkify -> CTDOMPurify.sanitize -> dangerouslySetInnerHTML
// (GameplayRefactor.renderTrustedMessageHTML). There is no markdown step and
// never has been, so a markdown image authored in the builder reaches players as the literal text
// "![Image](data:image/png;base64,…)". An <img> tag survives sanitize with its data URL intact, so
// authored images are stored as HTML. Both editors run Markdown.configure({ html: true }), so the
// tag still round-trips back into the builder as a real image node.
// Src has no spaces/parens (data URLs are base64; real URLs are encoded), which bounds the match.
const MARKDOWN_IMAGE = /!\[([^\]]*)\]\(([^\s)]+)\)/g
const attr = (v) => String(v).replace(/"/g, "&quot;")
export const markdownImagesToHtml = (source = "") =>
  source.replace(MARKDOWN_IMAGE, (_match, alt, src) => `<img src="${attr(src)}" alt="${attr(alt) || "Image"}" />`)

// A URL sitting inside an HTML tag — <video src="https://…mp4">, <img src="https://…png"> — must
// not be linkified. The player renders scenario text as linkify -> CTDOMPurify.sanitize ->
// dangerouslySetInnerHTML, so splicing an <a> into the attribute corrupts the tag before the
// sanitiser sees it and the whole element is dropped. Reachable from any pasted media URL; base64
// data: URLs only escaped it because the linkifier does not match them.
const isInsideTag = (text, index) => {
  const open = text.lastIndexOf("<", index)
  if (open === -1) return false
  const close = text.indexOf(">", open)
  return close === -1 || close > index
}

// Scene video -> a capped, clickable figure.
//
// The player renders scenario HTML straight into the feed, so a bare <video> paints at its
// intrinsic size (a 1080p clip overflows the column) and its native controls swallow clicks.
// Wrapping it lets CSS cap the frame and gives the click target for full-screen playback; the
// wrapper carries the src so the overlay does not have to read back into the sanitized DOM.
// The closing tag may be separated by whitespace or newlines by any producer that pretty-prints;
// absorbing only an immediately-adjacent one left an orphan </video> after the wrapper span.
const VIDEO_TAG = /<video\b([^>]*)>[\s\S]*?<\/video>|<video\b([^>]*)\/?>/gi
const SRC_ATTR = /\bsrc\s*=\s*"([^"]+)"/i
export const wrapSceneVideos = (source = "") =>
  source.replace(VIDEO_TAG, (match, attrsWithClose, attrsSelf) => {
    const attrs = attrsWithClose ?? attrsSelf ?? ""
    const src = (attrs.match(SRC_ATTR) || [])[1]
    if (!src) return match
    // The src is MOVED to the wrapper, not copied: a scenario-media URL needs an Authorization
    // header that a <video> tag cannot send, so leaving it in place makes the browser fire a
    // request that 401s and paints an empty player. The host resolves it to a signed URL and
    // assigns it (resolveSceneVideoSources below). A plain https link is put straight back.
    const inner = match.replace(SRC_ATTR, "").replace("<video", '<video preload="metadata"')
    // data-ct-media-key is the STABLE identity of this clip. data-video-src beside it is rewritten
    // to the signed URL once resolved, and a signed URL is different on every re-sign — so anything
    // that has to be remembered about a clip (has it been watched, how many times has it been
    // replayed) cannot be keyed on it.
    return `<span class="ct-scene-video" data-ct-media-key="${src}" data-video-src="${src}" role="button" tabindex="0" aria-label="Play video full screen">${inner}<span class="ct-scene-video-play" aria-hidden="true"></span></span>`
  })

// Scenario media is served behind an authenticated redirect, so its signed URL has to be fetched
// with a token and assigned. Anything else (a pasted https link) is already loadable and is set
// as-is. Resolved video URLs are written back to the wrapper so the full-screen overlay reuses them.
//
// Images go through the same redirect as video now that they are uploaded rather than base64
// inlined, so they need the same treatment: an <img src="/api/scenario-media/…"> would 401 exactly
// as <video> did. Legacy base64 images carry a data: URI and are left alone.
const MEDIA_PATH = "/scenario-media/"

const resolveOne = async ({ el, stored, getSignedUrl, onResolved }) => {
  if (!stored) return
  if (!stored.includes(MEDIA_PATH)) {
    el.setAttribute("src", stored)
    return
  }
  try {
    const signed = await getSignedUrl(stored)
    if (!signed) {
      // Loud on purpose. Swallowing this is what turned a wrong request URL into "the image is
      // just broken" with nothing in the console to chase.
      console.warn(`[scene-media] could not sign ${stored} — the element will stay blank`)
      return
    }
    el.setAttribute("src", signed)
    onResolved?.(signed)
  } catch (error) {
    // Leave the element empty rather than throwing inside a render effect; the rest of the
    // scene still shows.
    console.warn(`[scene-media] signing threw for ${stored}:`, error)
  }
}

export const resolveSceneMediaSources = async ({ root, getSignedUrl }) => {
  if (!root) return

  for (const wrapper of root.querySelectorAll?.(".ct-scene-video[data-video-src]") ?? []) {
    const video = wrapper.querySelector("video")
    if (!video || video.getAttribute("src")) continue
    await resolveOne({
      el: video,
      stored: wrapper.getAttribute("data-video-src"),
      getSignedUrl,
      // The overlay opens from this attribute, so point it at the playable URL too.
      onResolved: (signed) => wrapper.setAttribute("data-video-src", signed),
    })
  }

  const pending = [
    ...(root.querySelectorAll?.(`img[src*="${MEDIA_PATH}"]`) ?? []),
    ...(root.querySelectorAll?.("img[data-ct-media-src]:not([src])") ?? []),
  ]
  for (const img of pending) {
    const stored = img.getAttribute("src") || img.getAttribute("data-ct-media-src")
    // Parked on a data attribute rather than dropped: clearing src stops the browser firing a
    // request that 401s, but if signing then fails the element must still be findable, or one
    // transient token failure blanks the image for good with no retry.
    img.setAttribute("data-ct-media-src", stored)
    img.removeAttribute("src")
    await resolveOne({ el: img, stored, getSignedUrl })
    // Thumbnails open full screen on click, so they are controls and need to be reachable without
    // a mouse. Video already carries these on its wrapper.
    img.setAttribute("role", "button")
    img.setAttribute("tabindex", "0")
    if (!img.getAttribute("aria-label")) img.setAttribute("aria-label", "Open image full screen")
  }
}

// Markdown blocks each attachment separately — a <video> serializes as its own HTML block and an
// image as its own <p> — so inline-block never got the chance to put them on one line and they
// stacked vertically down the scene. Moving them into a single flex row is done on the real DOM
// rather than on the HTML string because the producers (markdown-it, TipTap) each block them
// differently, and the row is also the one element that carries the configurable thumbnail size.
//
// Every img inside a scene body is an attachment — the thumbnail CSS has always assumed that — so
// they are all collected, in document order, at the position of the first one.
export const groupSceneMedia = (root) => {
  if (!root?.querySelectorAll) return
  const items = [...root.querySelectorAll(".ct-scene-video, img")].filter((el) => !el.parentElement?.classList?.contains("ct-scene-media-row"))
  if (!items.length) return

  const row = root.ownerDocument.createElement("div")
  row.className = "ct-scene-media-row"
  // Anchor on the first item's outermost block inside the scene body, not on the item itself: a
  // <div> inserted inside the <p> that held the image is invalid nesting, and it also hid the now-
  // empty paragraph from the cleanup below (the paragraph still "contained" the image, via the row).
  let anchor = items[0]
  while (anchor.parentElement && anchor.parentElement !== root) anchor = anchor.parentElement
  anchor.parentNode?.insertBefore(row, anchor)

  const parents = new Set()
  for (const el of items) {
    parents.add(el.parentElement)
    row.appendChild(el)
  }

  // A <p> that held nothing but the image would otherwise stay behind as a blank line.
  for (const parent of parents) {
    if (!parent || parent === row || parent === root) continue
    if (!parent.textContent.trim() && !parent.querySelector("img, video")) parent.remove()
  }
}

// How many media items an artifact already carries. Counted from the stored markup rather than
// the editor, so it works in read-only mode too (where the RTE is not mounted). Only used to
// enforce the single-image limit of the no-storage fallback — with storage there is no cap.
export const countSceneMedia = (html) => {
  if (typeof html !== "string" || !html) return 0
  return (html.match(/<(img|video)\b/gi) ?? []).length
}

// String-level resolution, for renderers that own their DOM.
//
// The builder's scene card and the RTE render through TipTap, which rebuilds the document from the
// HTML it is given — mutating an <img> afterwards would be overwritten on the next setContent. So
// the signed URLs are substituted into the markup BEFORE it reaches the editor, rather than into
// the rendered nodes as resolveSceneMediaSources does for the player.
const MEDIA_SRC_ATTR = /src="([^"]*\/scenario-media\/[^"]*)"/g

export const resolveSceneMediaInText = async ({ html, getSignedUrl }) => {
  if (typeof html !== "string" || !html.includes(MEDIA_PATH)) return html
  const stored = [...new Set([...html.matchAll(MEDIA_SRC_ATTR)].map((m) => m[1]))]
  if (!stored.length) return html

  // In parallel: serially, a scene with ten attachments meant ten sequential round trips before
  // the first pixel. One failure still only costs its own image.
  const signed = new Map()
  await Promise.all(
    stored.map(async (url) => {
      try {
        const resolved = await getSignedUrl(url)
        if (resolved) signed.set(url, resolved)
      } catch {
        // Leave this one unresolved; the rest of the document still renders.
      }
    })
  )
  return html.replace(MEDIA_SRC_ATTR, (match, url) => (signed.has(url) ? `src="${signed.get(url)}"` : match))
}

// Kept as the old name so existing callers and tests continue to work.
export const resolveSceneVideoSources = resolveSceneMediaSources

export const renderHTMLLinksInStringForDisplay = ({ source, replacer = (href) => `<a href="${href}" target="_blank">${href}</a>` }) => {
  let resolvedSource = unescapeHTMLEntities(source)
  // let resolvedSource = decodeURIComponent(source)
  // Parse URLs
  const links = linkifier.match(source)
  if (links) {
    for (const { index, lastIndex, url } of links) {
      if (isInsideTag(source, index)) continue
      // Replace the range with a link
      resolvedSource = stringSplice({
        str: unescapeHTMLEntities(resolvedSource),
        index,
        count: lastIndex - index,
        add: replacer(url),
      })
    }
  }
  return resolvedSource
}

// Expanded RFC3986 URL component encoder
export const encodeRFC3986URIComponent = (str) => {
  return encodeURIComponent(str).replace(/[!'()*\/]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`)
}

/**
 * Format bytes as human-readable text.
 *
 * @param bytes Number of bytes.
 * @param si True to use metric (SI) units, aka powers of 1000. False to use
 *           binary (IEC), aka powers of 1024.
 * @param dp Number of decimal places to display.
 *
 * @return Formatted string.
 *
 * From: https://stackoverflow.com/a/14919494/2333933
 */
export const humanFileSize = (bytes, si = false, dp = 1) => {
  const thresh = si ? 1000 : 1024

  if (Math.abs(bytes) < thresh) {
    return bytes + " B"
  }

  const units = si ? ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"] : ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"]
  let u = -1
  const r = 10 ** dp

  do {
    bytes /= thresh
    ++u
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1)

  return bytes.toFixed(dp) + " " + units[u]
}

export const booleanFromString = (stringValue) => {
  let boolVal = false
  if (typeof stringValue === "string") {
    const normValue = stringValue.toLowerCase().trim()
    if (["yes", "true", "1"].includes(normValue)) {
      boolVal = true
    }
  }
  return boolVal
}

export const getFileMediaType = (file) => {
  if (!file || !file.type) return ""

  const supportedTypes = {
    "application/pdf": "application/pdf",
    "image/png": "image/png",
    "image/jpeg": "image/jpeg",
    "image/jpg": "image/jpg", // for .jpg files
    "text/plain": "text/plain",
    // Word runbooks/IR plans — the builder extracts the text server-side.
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  }

  // Return exact match if found
  if (supportedTypes[file.type]) {
    return supportedTypes[file.type]
  }

  // Special handling: if file is jpeg but has .jpg extension
  const fileName = file.name.toLowerCase()
  if (file.type === "image/jpeg" && fileName.endsWith(".jpg")) {
    return "image/jpg"
  }

  return ""
}

// Per-type upload caps, mirrored by the builder (validation on both sides). Text-bearing
// files (txt/docx) get the 20 MB umbrella; PDF and image caps are the Bedrock Converse
// attachment limits — bigger files would be accepted here only to fail downstream with
// a raw provider error.
export const UPLOAD_LIMITS_MB = {
  "application/pdf": 4.5,
  "image/png": 3.75,
  "image/jpeg": 3.75,
  "image/jpg": 3.75,
  "text/plain": 20,
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": 20,
}

// Pre-flight check for extraction uploads: format + size, with a user-readable error.
// Callers toast `error` and skip the upload when `ok` is false.
export const validateUploadFile = (file) => {
  if (!file) return { ok: false, error: "No file selected." }
  const mediaType = getFileMediaType(file)
  if (!mediaType) {
    return { ok: false, error: `"${file.name}" isn't a supported format. Upload a PDF, Word (.docx), text, PNG, or JPG file.` }
  }
  if (file.size === 0) {
    return { ok: false, error: `"${file.name}" is empty (0 KB). Please upload a valid file.` }
  }
  const limitMb = UPLOAD_LIMITS_MB[mediaType]
  if (limitMb && file.size > limitMb * 1_000_000) {
    return {
      ok: false,
      error: `"${file.name}" is ${(file.size / 1_000_000).toFixed(1)} MB — the limit for this file type is ${limitMb} MB.`,
    }
  }
  return { ok: true, mediaType }
}

export const standardizeLength = ({ stringLength, decimalDigits = 0, style = "decimal", unit = "" }) => {
  const options = {
    maximumFractionDigits: decimalDigits,
    notation: "compact",
    style: style,
  }
  if (style === "unit" && unit) {
    options.unit = unit
    options.unitDisplay = "short"
  }
  const formatter = new Intl.NumberFormat("en-US", options)
  return formatter.format(stringLength)
}

// Convert the RTE's stored content (markdown with embedded HTML — tiptap-markdown is configured
// with html: true, so tables come back as <table>...<tr>...<td>...</td></tr></table>) to plain
// text suitable for the LLM. Table row shape is preserved (cells joined by " | "). Idempotent:
// already-stripped output goes through unchanged.
export const markdownToPlainText = (input) => {
  if (!input || typeof input !== "string") return ""
  let text = input
  // 0) Decode HTML entities FIRST. Doing this before the tag passes means any escaped markup the
  //    user typed (e.g. "&lt;img onerror=x&gt;") decodes to real tags and is then removed by the
  //    strip passes below — instead of surviving the strip and reappearing as live markup. It also
  //    keeps the function idempotent: a second pass has no entities left to re-decode, so it can't
  //    re-create tags (updateSimulation re-strips on every save, so idempotency is required).
  text = text
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/&middot;/gi, "·")
    .replace(/&hellip;/gi, "…")
    .replace(/&mdash;/gi, "—")
    .replace(/&ndash;/gi, "–")
  // Decode numeric character refs (decimal & hex) too, so escaped markup like "&#x3c;img …&#x3e;"
  // decodes to a real tag here and is removed by the strip passes below — instead of slipping
  // through and reappearing as live markup downstream.
  text = decodeNumericHtmlEntities(text)
  // 1) HTML tables -> rows joined by "\n", cells in each row joined by " | "
  text = text.replace(/<table\b[^>]*>([\s\S]*?)<\/table>/gi, (_m, body) => {
    const rows = []
    body.replace(/<tr\b[^>]*>([\s\S]*?)<\/tr>/gi, (_rm, rowBody) => {
      const cells = []
      rowBody.replace(/<t[hd]\b[^>]*>([\s\S]*?)<\/t[hd]>/gi, (_cm, cell) => {
        // Match only real HTML tags (name starts with a letter or '/'), so decoded prose angle
        // brackets like "< 5ms >" are preserved rather than stripped. Strip to a fixed point so
        // tags can't be reconstructed (e.g. "<t<b>d>").
        const cellText = stripHtmlTagsToFixedPoint(cell, " ").replace(/\s+/g, " ").trim()
        cells.push(cellText)
      })
      if (cells.length) rows.push(cells.join(" | "))
    })
    return "\n" + rows.join("\n") + "\n"
  })
  // 2) Convert block-level / line-break tags to newlines BEFORE the blanket strip, so list items
  //    and paragraphs aren't concatenated. Without this, "<li>Okta</li><li>VPN</li>" would strip to
  //    "OktaVPN"; with it, it becomes "Okta\nVPN". (tiptap-markdown emits <ul>/<ol> for real lists.)
  text = text.replace(/<\s*br\s*\/?>/gi, "\n").replace(/<\/\s*(?:p|li|tr|div|h[1-6]|blockquote|ul|ol)\s*>/gi, "\n")
  // 3) Strip any remaining HTML tags. Anchor to a tag-name start ('<' then a letter or '</') so
  //    that decoded prose angle brackets survive — e.g. "latency < 5ms > 1ms" must NOT be eaten as
  //    if "< 5ms >" were a tag, while real/decoded tags ("<img onerror=x>", "</p>") still strip.
  //    Loop to a fixed point so tags can't be reconstructed by an earlier removal ("<scr<b>ipt>").
  text = stripHtmlTagsToFixedPoint(text)
  // 4) Markdown line-by-line cleanup (headings, lists, blockquotes, table rows, separators, HR)
  const out = []
  for (const raw of text.split(/\r?\n/)) {
    if (/^\s*```/.test(raw)) continue
    if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(raw)) continue
    if (raw.includes("|") && /^[\s|:-]+$/.test(raw) && raw.includes("-")) continue
    let line = raw
    if (/^\s*\|.*\|\s*$/.test(line)) {
      out.push(
        line
          .trim()
          .replace(/^\|/, "")
          .replace(/\|$/, "")
          .split("|")
          .map((cell) => cell.trim())
          .join(" | ")
      )
      continue
    }
    // Headings: strip the # marker but stop further line-marker stripping so section numbering
    // like "# 1. Company Overview" keeps the "1." (otherwise it gets eaten as a list marker).
    const headingMatch = line.match(/^\s{0,3}#{1,6}\s+(.*)$/)
    if (headingMatch) {
      out.push(headingMatch[1])
      continue
    }
    // Strip leading blockquote markers only. List markers ('- ', '1. ') are intentionally NOT
    // stripped: tiptap-markdown outputs lists as HTML <ul>/<ol> when html:true, so markdown list
    // markers rarely appear here, and stripping them would (a) eat section numbering inside
    // headings like '# 1. Company Overview' (after the '#' is removed the '1.' looks like a list
    // marker) and (b) break idempotency, which matters because updateSimulation strips on every save.
    line = line.replace(/^\s*>+\s?/, "")
    out.push(line)
  }
  text = out.join("\n")
  // 5) Inline markdown: images, links, emphasis, inline code
  text = text.replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
  text = text.replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
  text = text.replace(/`+/g, "")
  text = text.replace(/\*\*([^*]+)\*\*/g, "$1")
  text = text.replace(/\*([^*]+)\*/g, "$1")
  return text.replace(/\n{3,}/g, "\n\n").trim()
}

export const getBrowserLocale = () => {
  if (typeof navigator !== "undefined" && navigator.language) {
    return navigator.language
  }
  if (typeof navigator !== "undefined" && navigator.languages && navigator.languages.length > 0) {
    return navigator.languages[0]
  }
  return "en-US"
}

export const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch (error) {
    return "UTC"
  }
}

const formatDateWithIntl = (date, options = {}) => {
  if (!date) return ""
  try {
    const dateObj = date instanceof Date ? date : new Date(date)
    if (isNaN(dateObj.getTime())) return ""

    const locale = getBrowserLocale()
    const timeZone = getBrowserTimezone()

    const defaultOptions = {
      timeZone,
      ...options,
    }

    const formatter = new Intl.DateTimeFormat(locale, defaultOptions)
    return formatter.format(dateObj)
  } catch (error) {
    console.error("Error formatting date:", error)
    return ""
  }
}

export const formatDateLocale = (date) => {
  return formatDateWithIntl(date, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })
}

export const formatDateTimeLocale = (date) => {
  return formatDateWithIntl(date, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })
}

export const formatTimeLocale = (date) => {
  return formatDateWithIntl(date, {
    hour: "2-digit",
    minute: "2-digit",
  })
}

export const formatDateShortLocale = (date) => {
  return formatDateWithIntl(date, {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  })
}

export const formatDateWithOptions = (date, options = {}) => {
  return formatDateWithIntl(date, options)
}

// A scenario's info["ID"] is not a single shape: it may be "<prefix>-<simId>"
// ("CHT-010"), a bare "<simId>" ("10"), an already-numeric id, or a slug that
// merely contains hyphens ("adaptive-e2e-cyber-001"). Naive split("-")[1] yields
// NaN on the last two, which navigates the customizer to /customizer/NaN.
// Resolve by taking the LAST numeric run in the string.
export const simulationIdFromScenarioInfoId = (rawId) => {
  if (typeof rawId === "number" && Number.isFinite(rawId)) return rawId
  const asString = String(rawId ?? "").trim()
  if (!asString) return null
  const direct = Number(asString)
  if (Number.isInteger(direct)) return direct
  const numericRuns = asString.match(/\d+/g)
  if (!numericRuns?.length) return null
  return parseInt(numericRuns[numericRuns.length - 1], 10)
}
